import { type AgenticLogicSystemRuleKey } from "@intelligems/ig-types/dtos/agentic-logic/rules";
import { type EnsureAgenticLogicSystemRuleResponseBodyType } from "@intelligems/ig-types/functions/agentic-logic/rules/ensure-system";
import { queryOptions, useQuery } from "@tanstack/react-query";

import { api } from "../api";
import { agenticLogicKeys } from "../queryKeys";

const ensureAgenticLogicSystemRule = async (
  systemKey: AgenticLogicSystemRuleKey
) => {
  const resp = await api.put<EnsureAgenticLogicSystemRuleResponseBodyType>(
    `/v2/agentic-logic/system-rules/${systemKey}`,
    undefined
  );
  return resp.data;
};

/**
 * Where a store's built-in rule is on its way to serving.
 *
 * - `provisioning`: the rule is being created, or exists and its first version
 *   is still publishing. It can be selected and saved, but serves nothing yet.
 * - `ready`: the rule is live.
 * - `failed`: provisioning or its first publish failed.
 *
 * The rule row calls both "publishing" and "publish failed" `draft`, so the
 * latest publish job is what separates them.
 */
export type AgenticLogicSystemRulePhase = "provisioning" | "ready" | "failed";

export const agenticLogicSystemRulePhase = (
  data: EnsureAgenticLogicSystemRuleResponseBodyType | undefined,
  isError: boolean
): AgenticLogicSystemRulePhase => {
  if (isError) {
    return "failed";
  }
  if (!data) {
    return "provisioning";
  }
  if (data.rule.state === "active") {
    return "ready";
  }
  if (data.latestPublishJob?.status === "failed") {
    return "failed";
  }
  return "provisioning";
};

/** How often a provisioning built-in rule is re-read until it goes live. */
export const AGENTIC_LOGIC_SYSTEM_RULE_POLL_MS = 3_000;

/**
 * The store's built-in rule for `systemKey`, provisioned by the first call.
 *
 * The endpoint is an idempotent PUT: the first call for a store creates the
 * rule and enqueues its first publish, and every later call is a read. So it is
 * safe to issue from a query, from every surface that needs the rule, as often
 * as the query refetches. While the rule is provisioning the query polls, so
 * a picker showing it selected flips to live without a reload.
 *
 * Shared by {@link useAgenticLogicSystemRule} and the checkout-blocks listener
 * that binds a new upsell block to the rule, so both read one cache entry.
 */
export const agenticLogicSystemRuleQueryOptions = (
  systemKey: AgenticLogicSystemRuleKey
) =>
  queryOptions<EnsureAgenticLogicSystemRuleResponseBodyType, Error>({
    queryKey: agenticLogicKeys.systemRule(systemKey),
    queryFn: async () => ensureAgenticLogicSystemRule(systemKey),
    refetchInterval: (current) =>
      agenticLogicSystemRulePhase(
        current.state.data,
        current.state.status === "error"
      ) === "provisioning" && current.state.data
        ? AGENTIC_LOGIC_SYSTEM_RULE_POLL_MS
        : false,
  });

export const useAgenticLogicSystemRule = (
  systemKey: AgenticLogicSystemRuleKey,
  options: { enabled: boolean }
) => {
  const query = useQuery({
    ...agenticLogicSystemRuleQueryOptions(systemKey),
    enabled: options.enabled,
  });
  return {
    rule: query.data?.rule ?? null,
    phase: agenticLogicSystemRulePhase(query.data, query.isError),
  };
};
